import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Typography, InputNumber, Space, message, Divider, Checkbox, Table, Tag } from 'antd';
import {
    DownloadOutlined,
    ToolOutlined,
    BarChartOutlined,
    DatabaseOutlined,
    ReloadOutlined,
    DiffOutlined,
    SaveOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { FormattedMessage, useIntl } from 'react-intl';

const { Title, Text, Paragraph } = Typography;

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

interface BaselineRow {
    subsystem: string;
    version_number: number;
    status: string;
    keys_captured: number;
    table_rows_captured: number;
    created_at: string;
}

interface BaselineStatus {
    seededSubsystems: number;
    totalSubsystems: number;
    rows: BaselineRow[];
}

interface SeedResultRow {
    subsystem: string;
    status: 'created' | 'already_exists' | 'failed';
    versionId?: number;
    error?: string;
}

interface ParityDiff {
    key: string;
    kind:
        | 'fixture_added'
        | 'fixture_removed'
        | 'raw_text_changed'
        | 'resolved_text_changed'
        | 'empty_state_changed';
    details?: string;
}

interface ParityCompareResult {
    ok: boolean;
    fixtureCount: number;
    diffs: ParityDiff[];
    baselineCapturedAt?: string;
}

export const AdminToolsPage: React.FC = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const [minResponses, setMinResponses] = useState<number>(50);
    const [includeMock, setIncludeMock] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const [baselineStatus, setBaselineStatus] = useState<BaselineStatus | null>(null);
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);
    const [isSeedingBaseline, setIsSeedingBaseline] = useState(false);

    const [parityResult, setParityResult] = useState<ParityCompareResult | null>(null);
    const [isCapturingParity, setIsCapturingParity] = useState(false);
    const [isComparingParity, setIsComparingParity] = useState(false);

    const fetchBaselineStatus = useCallback(async () => {
        if (!user?.jwtToken) return;
        setIsLoadingStatus(true);
        try {
            const response = await fetch(
                `${apiBaseUrl}admin/tools/ai-admin/baseline-status`,
                { headers: { Authorization: `Bearer ${user.jwtToken}` } },
            );
            if (!response.ok) {
                throw new Error(`Status fetch failed (${response.status})`);
            }
            const data: BaselineStatus = await response.json();
            setBaselineStatus(data);
        } catch (error: any) {
            message.error(error.message || 'Failed to load baseline status');
        } finally {
            setIsLoadingStatus(false);
        }
    }, [user?.jwtToken]);

    useEffect(() => {
        fetchBaselineStatus();
    }, [fetchBaselineStatus]);

    const handleParityCompare = async () => {
        if (!user?.jwtToken) return;
        setIsComparingParity(true);
        try {
            const response = await fetch(
                `${apiBaseUrl}admin/tools/ai-admin/parity/compare`,
                { headers: { Authorization: `Bearer ${user.jwtToken}` } },
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || `Compare failed (${response.status})`);
            }
            const data: ParityCompareResult = await response.json();
            setParityResult(data);
            if (data.ok) {
                message.success(
                    intl.formatMessage(
                        { id: 'admin.tools.aiParity.passed', defaultMessage: 'Parity check passed — {count} fixtures match baseline.' },
                        { count: data.fixtureCount },
                    ),
                );
            } else {
                message.warning(
                    intl.formatMessage(
                        { id: 'admin.tools.aiParity.failed', defaultMessage: 'Drift detected: {count} fixtures differ from baseline.' },
                        { count: data.diffs.length },
                    ),
                );
            }
        } catch (error: any) {
            message.error(error.message || 'Parity compare failed');
        } finally {
            setIsComparingParity(false);
        }
    };

    const handleParityCapture = async () => {
        if (!user?.jwtToken) return;
        setIsCapturingParity(true);
        try {
            const response = await fetch(
                `${apiBaseUrl}admin/tools/ai-admin/parity/capture`,
                {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${user.jwtToken}` },
                },
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || `Capture failed (${response.status})`);
            }
            const data: { capturedAt: string; fixtureCount: number } = await response.json();
            message.success(
                intl.formatMessage(
                    { id: 'admin.tools.aiParity.captured', defaultMessage: 'Baseline captured: {count} fixtures.' },
                    { count: data.fixtureCount },
                ),
            );
            setParityResult({ ok: true, fixtureCount: data.fixtureCount, diffs: [], baselineCapturedAt: data.capturedAt });
        } catch (error: any) {
            message.error(error.message || 'Parity capture failed');
        } finally {
            setIsCapturingParity(false);
        }
    };

    const handleParityExport = async () => {
        if (!user?.jwtToken) return;
        try {
            const response = await fetch(
                `${apiBaseUrl}admin/tools/ai-admin/parity/baseline`,
                { headers: { Authorization: `Bearer ${user.jwtToken}` } },
            );
            if (!response.ok) {
                throw new Error(`Export failed (${response.status})`);
            }
            const data: { baseline: any } = await response.json();
            if (!data.baseline) {
                message.warning('No baseline captured yet.');
                return;
            }
            const blob = new Blob([JSON.stringify(data.baseline, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ai-parity-baseline-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            message.error(error.message || 'Export failed');
        }
    };

    const handleSeedBaseline = async () => {
        if (!user?.jwtToken) {
            message.error(intl.formatMessage({ id: 'admin.tools.notAuthenticated', defaultMessage: 'Not authenticated' }));
            return;
        }
        setIsSeedingBaseline(true);
        try {
            const response = await fetch(
                `${apiBaseUrl}admin/tools/ai-admin/seed-v1-baseline`,
                {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${user.jwtToken}` },
                },
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || `Seed failed (${response.status})`);
            }
            const data: { summary: { created: number; already_exists: number; failed: number }; results: SeedResultRow[] } = await response.json();
            const { created, already_exists, failed } = data.summary;
            if (failed > 0) {
                const failedRow = data.results.find((r) => r.status === 'failed');
                message.error(
                    intl.formatMessage(
                        {
                            id: 'admin.tools.aiBaseline.seedFailed',
                            defaultMessage: 'Seed had {failed} failure(s). First: {subsystem}: {error}',
                        },
                        {
                            failed,
                            subsystem: failedRow?.subsystem,
                            error: failedRow?.error,
                        },
                    ),
                );
            } else {
                message.success(
                    intl.formatMessage(
                        {
                            id: 'admin.tools.aiBaseline.seedSuccess',
                            defaultMessage: '{created} subsystems seeded, {existing} already existed.',
                        },
                        { created, existing: already_exists },
                    ),
                );
            }
            await fetchBaselineStatus();
        } catch (error: any) {
            message.error(error.message || 'Seed request failed');
        } finally {
            setIsSeedingBaseline(false);
        }
    };

    const handleExportStatementScores = async () => {
        if (!user?.jwtToken) {
            message.error(intl.formatMessage({ id: 'admin.tools.notAuthenticated', defaultMessage: 'Not authenticated' }));
            return;
        }

        setIsExporting(true);
        try {
            const response = await fetch(
                `${apiBaseUrl}admin/export/statement-scores?minResponses=${minResponses}&includeMock=${includeMock}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${user.jwtToken}`,
                    },
                },
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || `Export failed (${response.status})`);
            }

            const blob = await response.blob();
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch?.[1] || `statement_scores_${new Date().toISOString().split('T')[0]}.xlsx`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            message.success(intl.formatMessage({ id: 'admin.tools.exportSuccess', defaultMessage: 'Export downloaded successfully' }));
        } catch (error: any) {
            message.error(error.message || intl.formatMessage({ id: 'admin.tools.exportError', defaultMessage: 'Export failed' }));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div>
            <div style={{ marginBottom: 32 }}>
                <Title level={2}>
                    <ToolOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="admin.tools.title" defaultMessage="Admin Tools" />
                </Title>
                <Text type="secondary">
                    <FormattedMessage id="admin.tools.subtitle" defaultMessage="Data export and platform utilities" />
                </Text>
            </div>

            <Card>
                <Title level={4}>
                    <BarChartOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="admin.tools.statementScores.title" defaultMessage="Statement-Level Scores Export" />
                </Title>
                <Paragraph type="secondary">
                    <FormattedMessage
                        id="admin.tools.statementScores.description"
                        defaultMessage="Export an Excel spreadsheet with statement-level brand equity scores for all brands that have received a minimum number of survey responses (all time). Each brand gets its own sheet with category, statement text, mean score, polarity, intensity, brand power, and controversy metrics — all with classification labels."
                    />
                </Paragraph>

                <Divider />

                <Space direction="vertical" size="middle">
                    <Space align="center">
                        <Text>
                            <FormattedMessage id="admin.tools.statementScores.minResponses" defaultMessage="Minimum responses:" />
                        </Text>
                        <InputNumber
                            min={1}
                            max={10000}
                            value={minResponses}
                            onChange={(value) => setMinResponses(value || 50)}
                            style={{ width: 120 }}
                        />
                    </Space>

                    <Checkbox
                        checked={includeMock}
                        onChange={(e) => setIncludeMock(e.target.checked)}
                    >
                        <FormattedMessage id="admin.tools.statementScores.includeMock" defaultMessage="Include mock brands" />
                    </Checkbox>

                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={isExporting}
                        onClick={handleExportStatementScores}
                        size="large"
                    >
                        <FormattedMessage id="admin.tools.statementScores.exportButton" defaultMessage="Export Statement Scores" />
                    </Button>
                </Space>
            </Card>

            <Card style={{ marginTop: 24 }}>
                <Title level={4}>
                    <DatabaseOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage
                        id="admin.tools.aiBaseline.title"
                        defaultMessage="AI Admin Overhaul — v1 Baseline Seeding"
                    />
                </Title>
                <Paragraph type="secondary">
                    <FormattedMessage
                        id="admin.tools.aiBaseline.description"
                        defaultMessage="Captures the current production state of every AI subsystem (prompts, model configuration, RAG knobs, shared library content) as a v1 published version in ai_subsystem_version. Required after the AI Admin Overhaul deploys; idempotent — safe to re-run."
                    />
                </Paragraph>

                <Divider />

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Space>
                        <Text strong>
                            <FormattedMessage
                                id="admin.tools.aiBaseline.statusLabel"
                                defaultMessage="Seeded:"
                            />
                        </Text>
                        {baselineStatus ? (
                            <Tag
                                color={
                                    baselineStatus.seededSubsystems ===
                                    baselineStatus.totalSubsystems
                                        ? 'green'
                                        : baselineStatus.seededSubsystems === 0
                                            ? 'red'
                                            : 'orange'
                                }
                            >
                                {baselineStatus.seededSubsystems} / {baselineStatus.totalSubsystems}
                            </Tag>
                        ) : (
                            <Tag>—</Tag>
                        )}
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            loading={isLoadingStatus}
                            onClick={fetchBaselineStatus}
                        >
                            <FormattedMessage
                                id="admin.tools.aiBaseline.refresh"
                                defaultMessage="Refresh"
                            />
                        </Button>
                    </Space>

                    {baselineStatus && baselineStatus.rows.length > 0 && (
                        <Table
                            size="small"
                            rowKey="subsystem"
                            pagination={false}
                            dataSource={baselineStatus.rows}
                            columns={[
                                {
                                    title: 'Subsystem',
                                    dataIndex: 'subsystem',
                                    key: 'subsystem',
                                },
                                {
                                    title: 'Version',
                                    dataIndex: 'version_number',
                                    key: 'version_number',
                                    width: 90,
                                    render: (v: number) => `v${v}`,
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    width: 110,
                                    render: (s: string) => (
                                        <Tag color={s === 'published' ? 'green' : 'default'}>{s}</Tag>
                                    ),
                                },
                                {
                                    title: 'System Settings',
                                    dataIndex: 'keys_captured',
                                    key: 'keys_captured',
                                    width: 140,
                                },
                                {
                                    title: 'Config Tables',
                                    dataIndex: 'table_rows_captured',
                                    key: 'table_rows_captured',
                                    width: 130,
                                },
                                {
                                    title: 'Created',
                                    dataIndex: 'created_at',
                                    key: 'created_at',
                                    render: (v: string) =>
                                        v ? new Date(v).toLocaleString() : '—',
                                },
                            ]}
                        />
                    )}

                    <Button
                        type="primary"
                        icon={<DatabaseOutlined />}
                        loading={isSeedingBaseline}
                        onClick={handleSeedBaseline}
                        size="large"
                        disabled={
                            !!baselineStatus &&
                            baselineStatus.seededSubsystems === baselineStatus.totalSubsystems
                        }
                    >
                        {baselineStatus &&
                            baselineStatus.seededSubsystems === baselineStatus.totalSubsystems ? (
                            <FormattedMessage
                                id="admin.tools.aiBaseline.fullySeeded"
                                defaultMessage="All Subsystems Already Seeded"
                            />
                        ) : (
                            <FormattedMessage
                                id="admin.tools.aiBaseline.seedButton"
                                defaultMessage="Seed v1 Baseline"
                            />
                        )}
                    </Button>
                </Space>
            </Card>

            <Card style={{ marginTop: 24 }}>
                <Title level={4}>
                    <DiffOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage
                        id="admin.tools.aiParity.title"
                        defaultMessage="AI Parity Harness"
                    />
                </Title>
                <Paragraph type="secondary">
                    <FormattedMessage
                        id="admin.tools.aiParity.description"
                        defaultMessage="Captures resolved prompts for every AI prompt-shaped surface (system_settings, advice/orbital/building-blocks/dashboard configs, ai_experts persona prompts) and compares against a stored baseline to detect drift in DB content or in the placeholder resolver. Run capture once after deploy or after any intentional admin edit; then run compare regularly to catch silent drift."
                    />
                </Paragraph>

                <Divider />

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Space wrap>
                        <Button
                            type="primary"
                            icon={<DiffOutlined />}
                            loading={isComparingParity}
                            onClick={handleParityCompare}
                        >
                            <FormattedMessage
                                id="admin.tools.aiParity.compareButton"
                                defaultMessage="Compare against baseline"
                            />
                        </Button>
                        <Button
                            icon={<SaveOutlined />}
                            loading={isCapturingParity}
                            onClick={handleParityCapture}
                        >
                            <FormattedMessage
                                id="admin.tools.aiParity.captureButton"
                                defaultMessage="Capture new baseline"
                            />
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleParityExport}
                        >
                            <FormattedMessage
                                id="admin.tools.aiParity.exportButton"
                                defaultMessage="Export baseline JSON"
                            />
                        </Button>
                    </Space>

                    {parityResult && (
                        <>
                            <Space>
                                <Text strong>
                                    <FormattedMessage
                                        id="admin.tools.aiParity.resultLabel"
                                        defaultMessage="Result:"
                                    />
                                </Text>
                                <Tag color={parityResult.ok ? 'green' : 'red'}>
                                    {parityResult.ok
                                        ? intl.formatMessage({ id: 'admin.tools.aiParity.pass', defaultMessage: 'PASS' })
                                        : intl.formatMessage({ id: 'admin.tools.aiParity.fail', defaultMessage: 'DRIFT' })}
                                </Tag>
                                <Text type="secondary">
                                    {parityResult.fixtureCount} fixtures
                                    {parityResult.baselineCapturedAt && (
                                        <>
                                            {' '}· baseline captured {new Date(parityResult.baselineCapturedAt).toLocaleString()}
                                        </>
                                    )}
                                </Text>
                            </Space>

                            {parityResult.diffs.length > 0 && (
                                <Table
                                    size="small"
                                    rowKey={(row) => `${row.kind}:${row.key}`}
                                    pagination={{ pageSize: 25 }}
                                    dataSource={parityResult.diffs}
                                    columns={[
                                        {
                                            title: 'Kind',
                                            dataIndex: 'kind',
                                            key: 'kind',
                                            width: 200,
                                            render: (k: string) => {
                                                const colour =
                                                    k === 'fixture_added'
                                                        ? 'blue'
                                                        : k === 'fixture_removed'
                                                            ? 'orange'
                                                            : 'red';
                                                return <Tag color={colour}>{k}</Tag>;
                                            },
                                        },
                                        {
                                            title: 'Fixture',
                                            dataIndex: 'key',
                                            key: 'key',
                                        },
                                        {
                                            title: 'Details',
                                            dataIndex: 'details',
                                            key: 'details',
                                        },
                                    ]}
                                />
                            )}
                        </>
                    )}
                </Space>
            </Card>
        </div>
    );
};
